import React from 'react';
import { motion } from 'framer-motion';
import { ClockIcon, HeartIcon, ShieldCheckIcon, UsersIcon, AwardIcon, CheckCircle2Icon, DollarSignIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export function HeroSection() {
  return <section id="home" className="relative pt-32 pb-12 md:pt-40 md:pb-16 overflow-hidden min-h-[85vh] flex items-center bg-accent-light" data-id="element-125">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.03]" data-id="element-126">
        <svg className="h-full w-full" xmlns="http://www.w3.org/2000/svg" data-id="element-127">
          <defs data-id="element-128">
            <pattern id="hero-dots" width="32" height="32" patternUnits="userSpaceOnUse" data-id="element-129">
              <circle cx="2" cy="2" r="1.5" fill="currentColor" className="text-primary" data-id="element-130" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#hero-dots)" data-id="element-131" />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-4 relative z-10 w-full" data-id="element-132">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16" data-id="element-133">
          {/* Left: Text Content */}
          <div className="flex-1 max-w-2xl lg:max-w-none" data-id="element-134">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="inline-flex items-center gap-2 bg-white text-primary-dark px-4 py-1.5 rounded-full font-medium text-xs mb-4 shadow-sm border border-accent/20" data-id="element-135">
              <ClockIcon className="w-3.5 h-3.5 text-accent" data-id="element-136" />
              <span data-id="element-137">24-48 Hour Onboarding</span>
            </motion.div>

            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-neutral-dark leading-[1.1] mb-4" data-id="element-138">
              Your Family. <br data-id="element-139" />
              <span className="text-primary" data-id="element-140">Your Caregiver.</span> <br data-id="element-141" />
              Our Support.
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="text-base md:text-lg text-neutral-medium mb-4 leading-relaxed max-w-xl" data-id="element-142">
              Want your daughter, son, or nephew to be your caregiver? We help
              families hire, train, and certify their loved ones as professional
              caregivers.
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.25
          }} className="flex flex-col gap-2 bg-accent/10 border border-accent/20 p-3 rounded-xl mb-6 max-w-xl" data-id="element-143">
              <div className="flex items-start gap-3" data-id="element-144">
                <div className="bg-accent rounded-full p-1 mt-0.5" data-id="element-145">
                  <DollarSignIcon className="w-3.5 h-3.5 text-white" data-id="element-146" />
                </div>
                <p className="text-neutral-dark font-medium text-sm" data-id="element-147">
                  Did you know your family member can get paid to care for you?{' '}
                  <span className="text-primary font-bold" data-id="element-148">
                    We help them get certified and approved.
                  </span>
                </p>
              </div>
              <p className="text-[10px] text-neutral-medium italic pl-8" data-id="element-149">
                *If a family member is not available, we can connect you with
                our qualified professional caregivers.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} className="flex flex-col sm:flex-row gap-3" data-id="element-150">
              <Link to="/contact" className="bg-accent hover:bg-accent-dark text-neutral-dark px-6 py-3 rounded-lg font-semibold text-center transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 text-sm" data-id="element-151">
                <HeartIcon className="w-4 h-4" data-id="element-152" />
                Get Started Today
              </Link>
              <Link to="/how-it-works" className="bg-white hover:bg-gray-50 text-primary border-2 border-primary/20 hover:border-primary px-6 py-3 rounded-lg font-semibold text-center transition-all text-sm" data-id="element-153">
                Learn How It Works
              </Link>
            </motion.div>

            <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.8,
            delay: 0.6
          }} className="mt-6 flex items-center gap-4 text-xs font-medium text-neutral-medium" data-id="element-154">
              <div className="flex -space-x-2" data-id="element-155">
                {[1, 2, 3, 4].map(i => <img key={i} className="w-8 h-8 rounded-full border-2 border-white" src={`https://i.pravatar.cc/100?img=${i + 10}`} alt="Happy family member" data-id="element-156" />)}
              </div>
              <p data-id="element-157">Trusted by 500+ local families</p>
            </motion.div>

            {/* Explanation Section */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.7
          }} className="mt-8 pt-6 border-t border-accent/10" data-id="element-158">
              <h3 className="text-[10px] font-bold text-primary uppercase tracking-wider mb-3" data-id="element-159">
                Services Included:
              </h3>
              <div className="grid grid-cols-2 gap-y-2 gap-x-4" data-id="element-160">
                {['Mandatory background checks', 'Training', 'Certification', 'Approval process'].map(service => <div key={service} className="flex items-center gap-2" data-id="element-161">
                    <CheckCircle2Icon className="w-4 h-4 text-accent" data-id="element-162" />
                    <span className="text-neutral-dark font-medium text-xs" data-id="element-163">
                      {service}
                    </span>
                  </div>)}
              </div>
            </motion.div>
          </div>

          {/* Right: Visual Composition */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.3
        }} className="flex-1 relative hidden lg:block" data-id="element-164">
            {/* Main photo */}
            <div className="relative pr-8" data-id="element-165">
              <div className="rounded-3xl overflow-hidden shadow-2xl" data-id="element-166">
                <img src="https://cdn.magicpatterns.com/uploads/vdmWP8gfDfGa4fXZHxBUcR/image.png" alt="Caregiver pouring tea for an elderly patient in bed" className="w-full h-[400px] object-cover" data-id="element-167" />
              </div>

              {/* Decorative ring */}
              <div className="absolute -top-4 -right-2 w-24 h-24 rounded-full border-2 border-accent/20" data-id="element-168" />
              <div className="absolute -bottom-2 -left-2 w-16 h-16 rounded-full bg-accent/10" data-id="element-169" />

              {/* Floating stat card — top right */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.7
            }} className="absolute -top-2 -right-2 bg-white rounded-2xl shadow-lg p-3 flex items-center gap-3 border border-gray-100" data-id="element-170">
                <div className="w-8 h-8 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-171">
                  <ShieldCheckIcon className="w-4 h-4 text-accent" data-id="element-172" />
                </div>
                <div data-id="element-173">
                  <p className="font-heading font-bold text-neutral-dark text-base leading-tight" data-id="element-174">
                    RN-Supervised
                  </p>
                  <p className="text-[10px] text-neutral-medium" data-id="element-175">
                    Clinical Excellence
                  </p>
                </div>
              </motion.div>

              {/* Floating stat card — bottom left */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.9
            }} className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-lg p-3 flex items-center gap-3 border border-gray-100" data-id="element-176">
                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-177">
                  <UsersIcon className="w-4 h-4 text-primary" data-id="element-178" />
                </div>
                <div data-id="element-179">
                  <p className="font-heading font-bold text-neutral-dark text-base leading-tight" data-id="element-180">
                    500+
                  </p>
                  <p className="text-[10px] text-neutral-medium" data-id="element-181">
                    Families Served
                  </p>
                </div>
              </motion.div>

              {/* Floating stat card — bottom right */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 1.1
            }} className="absolute bottom-12 -right-4 bg-primary rounded-2xl shadow-lg p-3 flex items-center gap-3" data-id="element-182">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-183">
                  <AwardIcon className="w-4 h-4 text-accent" data-id="element-184" />
                </div>
                <div data-id="element-185">
                  <p className="font-heading font-bold text-white text-base leading-tight" data-id="element-186">
                    Certified
                  </p>
                  <p className="text-[10px] text-white/70" data-id="element-187">In 24-48 Hours</p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}